import React from 'react';
import PropTypes from 'prop-types';

import { LIST_TYPE } from 'eventbrite_design_system/mediaCardContent/constants';
import LoadingCards from 'eventbrite_design_system/eventCard/LoadingCards';

import { setMark } from '@eb/performance-monitor';

import SearchEventCardStyleSwitcher from './SearchEventCardStyleSwitcher';
import SearchNoResultsSuggestionsHeader from './SearchNoResultsSuggestionsHeader';
import SearchNoResultsSuggestionsEvents from './SearchNoResultsSuggestionsEvents';
import ConnectedSearchHeader from '../containers/ConnectedSearchHeader';

import {
    SHARE_OPTIONS_PROPTYPE,
    SEARCH_SECTION,
    GA_SEARCH_CATEGORY,
    SEARCH_EVENT_NAVIGATION,
    LUX_MARK_SEARCH_READY,
} from '../constants';

import './ResultsPanel.scss';

const _getCards = (
    content,
    type,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    hoverEventCardTarget,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
) =>
    content.map(({ id, ...cardProps }, index) => (
        <li key={id}>
            <div
                className="search-event-card-wrapper"
                onMouseEnter={onMouseOverCard.bind(null, id)}
                onFocus={onMouseOverCard.bind(null, id)}
                onMouseLeave={onMouseOutCard}
                onBlur={onMouseOutCard}
                role="presentation"
            >
                <SearchEventCardStyleSwitcher
                    id={id}
                    trackClick={trackClick.bind(null, index, id)}
                    type={LIST_TYPE}
                    shareOptions={shareOptions}
                    isAuthenticated={isAuthenticated}
                    onSaveEvent={onSaveEvent}
                    containerSpacingClasses={isMobile ? '' : 'eds-l-pad-all-4'}
                    trackClickInternal={trackClickInternal.bind(null, index)}
                    trackGAEvent={trackGAEvent}
                    section={SEARCH_SECTION}
                    featureFlags={featureFlags}
                    focused={hoverEventCardTarget === id}
                    userCollections={userCollections}
                    onAddOrRemoveEventToCollection={
                        onAddOrRemoveEventToCollection
                    }
                    onCreateCollection={onCreateCollection}
                    {...cardProps}
                />
            </div>
        </li>
    ));

const ResultGroup = ({
    content,
    type,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    hoverEventCardTarget,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
}) => (
    <section>
        <ul className="search-main-content__events-list">
            {_getCards(
                content,
                type,
                shareOptions,
                onSaveEvent,
                isAuthenticated,
                isMobile,
                trackClick,
                trackClickInternal,
                trackGAEvent,
                onMouseOverCard,
                onMouseOutCard,
                featureFlags,
                hoverEventCardTarget,
                userCollections,
                onAddOrRemoveEventToCollection,
                onCreateCollection,
            )}
        </ul>
    </section>
);

// This may be unnecessary. But in the future we may include
// more than just events, or display the search results in a
// different format.
const _parseContent = (
    results,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    hoverEventCardTarget,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
) =>
    results.map(({ content, type }) => {
        const key = `search_${type}`;

        const emptyResults = <div key={key} />;

        if (!content) {
            return emptyResults;
        }

        return (
            <ResultGroup
                key={key}
                content={content}
                type={type}
                shareOptions={shareOptions}
                onSaveEvent={onSaveEvent}
                isAuthenticated={isAuthenticated}
                isMobile={isMobile}
                trackClick={trackClick}
                trackClickInternal={trackClickInternal}
                trackGAEvent={trackGAEvent}
                onMouseOverCard={onMouseOverCard}
                onMouseOutCard={onMouseOutCard}
                featureFlags={featureFlags}
                hoverEventCardTarget={hoverEventCardTarget}
                userCollections={userCollections}
                onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                onCreateCollection={onCreateCollection}
            />
        );
    });

// Exporting for use in exp_eb_92146_integrated_content_on_search
export const SearchResults = ({
    results,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isLoading,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    hoverEventCardTarget,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
}) => {
    let component = <LoadingCards style={isMobile ? 'list-square' : 'list'} />;

    if (!isLoading) {
        component = (
            <div>
                {_parseContent(
                    results,
                    shareOptions,
                    onSaveEvent,
                    isAuthenticated,
                    isMobile,
                    trackClick,
                    trackClickInternal,
                    trackGAEvent,
                    onMouseOverCard,
                    onMouseOutCard,
                    featureFlags,
                    hoverEventCardTarget,
                    userCollections,
                    onAddOrRemoveEventToCollection,
                    onCreateCollection,
                )}
            </div>
        );
    }

    return component;
};

const SearchResultSection = ({
    searchResultProps,
    isLoading,
    hasEventResults,
    isMobile,
}) => {
    let component = <SearchResults {...searchResultProps} />;

    if (isMobile) {
        component = (
            <SearchResults
                {...searchResultProps}
                isMobile={true}
            />
        );
    }

    if (!isLoading && !hasEventResults) {
        component = <SearchNoResultsSuggestionsHeader />;
    }

    return component;
};

const NoResultsSuggestions = ({
    isLoading,
    hasEventResults,
    searchResultProps: {
        userCollections,
        onAddOrRemoveEventToCollection,
        onCreateCollection,
    },
    ...props
}) => {
    let component = null;

    if (!isLoading && !hasEventResults) {
        component = (
            <SearchNoResultsSuggestionsEvents
                userCollections={userCollections}
                onAddOrRemoveEventToCollection={onAddOrRemoveEventToCollection}
                onCreateCollection={onCreateCollection}
                {...props}
            />
        );
    }

    return component;
};

export default class ResultsPanel extends React.PureComponent {
    static propTypes = {
        results: PropTypes.arrayOf(PropTypes.object),
        shareOptions: SHARE_OPTIONS_PROPTYPE.isRequired,
        onSaveEvent: PropTypes.func,
        isAuthenticated: PropTypes.bool,
        onTrackEventClickInternal: PropTypes.func,
        hasEventResults: PropTypes.bool,
        trackGAEvent: PropTypes.func,
        // TODO: Add proptype shape
        eventSuggestions: PropTypes.arrayOf(PropTypes.object),
        allEventsUrl: PropTypes.string,
        userCollections: PropTypes.arrayOf(PropTypes.object),
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
    };

    state = {
        hoverEventCardTarget: null,
    };

    componentDidMount() {
        setMark(LUX_MARK_SEARCH_READY);
    }

    _handleTrackClick = (index, eventId, action) => {
        const { trackGAEvent } = this.props;

        trackGAEvent({
            action,
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
            },
        });
    };

    _handleTrackClickInternal = (index, id, ticketsBy) => {
        const { onTrackEventClickInternal } = this.props;

        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal(trackingData, ticketsBy);
    };

    _handleMouseOverCard = (eid) => {
        this.setState({ hoverEventCardTarget: eid });
    };

    _handleMouseOutCard = () => {
        this.setState({ hoverEventCardTarget: null });
    };

    render() {
        const {
            results,
            shareOptions,
            onSaveEvent,
            isAuthenticated,
            isLoading,
            hasEventResults,
            eventSuggestions,
            allEventsUrl,
            featureFlags,
            location,
            domain,
            trackGAEvent,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            onGoogleMapsLoaded,
            handleMouseOverCard,
            handleMouseOutCard,
        } = this.props;

        const { hoverEventCardTarget } = this.state;

        const searchResultProps = {
            results,
            shareOptions,
            onSaveEvent,
            isAuthenticated,
            isLoading,
            featureFlags,
            trackGAEvent,
            trackClick: this._handleTrackClick,
            trackClickInternal: this._handleTrackClickInternal,
            onMouseOverCard: handleMouseOverCard,
            onMouseOutCard: handleMouseOutCard,
            hoverEventCardTarget,
            userCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
        };

        const searchResultsProps = {
            searchResultProps,
            isLoading,
            hasEventResults,
            eventSuggestions,
            shareOptions,
            onSaveEvent,
            isAuthenticated,
            allEventsUrl,
            featureFlags,
            location,
            domain,
            trackGAEvent,
        };

        return (
            <main className="search-results-panel-content">
                <ConnectedSearchHeader
                    onGoogleMapsLoaded={onGoogleMapsLoaded}
                    history={this.props.history}
                />
                <SearchResultSection {...searchResultsProps} />
                <NoResultsSuggestions {...searchResultsProps} />
            </main>
        );
    }
}
